import type {
  LandmarkTeleportDestination,
} from "../world/landmarkDestinations.ts";
import {
  DEFAULT_WORLD_VIEW_SETTINGS,
  streamedTileCapacity,
  WORLD_VIEW_SETTING_LIMITS,
  type WorldViewMode,
  type WorldViewSettings,
  type WorldViewSettingKey,
} from "../config/viewSettings.ts";

export type DevMenuSeason = "spring" | "summer" | "autumn" | "winter";
export type DevMenuTraversalPreset = "locked" | "blueGate" | "rooftopShortcut";

export interface DevMenuViewModel {
  readonly season: DevMenuSeason;
  readonly timeOfDayHours: number;
  readonly frozen: boolean;
  readonly surfaceWetness01?: number;
  readonly activeViewMode: WorldViewMode;
  readonly viewSettings: Readonly<WorldViewSettings>;
  readonly traversalAccess: Readonly<{
    blueGate: boolean;
    rooftopShortcut: boolean;
  }>;
}

export interface DevMenuCallbacks {
  onOpenChange(open: boolean): void;
  onSetTime(hours: number): void;
  onSetSeason(season: DevMenuSeason): void;
  onSetSurfaceWetness(wetness01: number): void;
  onToggleClock(): void;
  onSetViewSetting(
    mode: WorldViewMode,
    key: WorldViewSettingKey,
    value: number,
  ): void;
  onResetViewProfile(mode: WorldViewMode): void;
  onSetTraversalPreset(preset: DevMenuTraversalPreset): void;
  onTeleport(destination: LandmarkTeleportDestination): Promise<void>;
}

export interface DevMenu {
  update(viewModel: Readonly<DevMenuViewModel>): void;
  toggle(): void;
  close(): void;
  isOpen(): boolean;
  dispose(): void;
}

const SEASONS: readonly Readonly<{
  id: DevMenuSeason;
  label: string;
}>[] = [
  { id: "spring", label: "Spring" },
  { id: "summer", label: "Summer" },
  { id: "autumn", label: "Autumn" },
  { id: "winter", label: "Winter" },
];

const TIME_PRESETS = [
  { label: "Dawn", hours: 6 },
  { label: "Day", hours: 10 },
  { label: "Dusk", hours: 20.5 },
  { label: "Night", hours: 23 },
] as const;

const VIEW_MODES: readonly Readonly<{
  id: WorldViewMode;
  label: string;
}>[] = [
  { id: "grounded", label: "On foot" },
  { id: "flight", label: "Flight" },
];

const TRAVERSAL_PRESETS: readonly Readonly<{
  id: DevMenuTraversalPreset;
  label: string;
}>[] = [
  { id: "locked", label: "Locked" },
  { id: "blueGate", label: "Blue gate" },
  { id: "rooftopShortcut", label: "Roof stairs" },
];

const VIEW_SETTING_CONTROLS: readonly Readonly<{
  key: WorldViewSettingKey;
  label: string;
}>[] = [
  { key: "streamedRadiusTiles", label: "Streamed area" },
  { key: "fullDetailRadiusM", label: "Near-detail range" },
  { key: "cameraFarMeters", label: "Camera range" },
  { key: "fogFarDayMeters", label: "Fog horizon" },
];

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  element.className = className;
  return element;
}

function normalizedHour(hours: number): number {
  return ((hours % 24) + 24) % 24;
}

export function formatTimeOfDay(hours: number): string {
  const totalMinutes = Math.round(normalizedHour(hours) * 60) % (24 * 60);
  const displayHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${displayHours.toString().padStart(2, "0")}:${
    minutes.toString().padStart(2, "0")
  }`;
}

function formatViewSetting(
  key: WorldViewSettingKey,
  value: number,
): string {
  if (key === "streamedRadiusTiles") {
    const diameter = value * 2 + 1;
    return `${diameter} × ${diameter} · ${streamedTileCapacity(value)} cells`;
  }
  return `${Math.round(value).toLocaleString("en-GB")} m`;
}

export function createDevMenu(
  root: HTMLElement,
  destinations: readonly Readonly<LandmarkTeleportDestination>[],
  callbacks: Readonly<DevMenuCallbacks>,
): DevMenu {
  if (destinations.length === 0) {
    throw new Error("The development menu requires landmark destinations.");
  }

  const toggleButton = createElement("button", "dev-menu-toggle");
  toggleButton.type = "button";
  toggleButton.setAttribute("aria-label", "Open City Lab scene controls");
  toggleButton.setAttribute("aria-expanded", "false");
  toggleButton.setAttribute("aria-controls", "dev-menu-panel");
  const toggleKicker = createElement("span", "dev-menu-toggle-kicker");
  toggleKicker.textContent = "City lab";
  const toggleKey = createElement("kbd", "dev-menu-toggle-key");
  toggleKey.textContent = "`";
  toggleButton.append(toggleKicker, toggleKey);

  const panel = createElement("section", "dev-menu-panel");
  panel.id = "dev-menu-panel";
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("aria-labelledby", "dev-menu-title");

  const header = createElement("header", "dev-menu-header");
  const headingGroup = createElement("div", "dev-menu-heading-group");
  const eyebrow = createElement("p", "dev-menu-eyebrow");
  eyebrow.textContent = "Development fieldbook";
  const title = createElement("h2", "dev-menu-title");
  title.id = "dev-menu-title";
  title.textContent = "Scene controls";
  headingGroup.append(eyebrow, title);
  const closeButton = createElement("button", "dev-menu-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close scene controls");
  closeButton.textContent = "Close";
  header.append(headingGroup, closeButton);

  const timeSection = createElement("section", "dev-menu-section");
  const timeHeadingRow = createElement("div", "dev-menu-section-heading");
  const timeLabel = createElement("label", "dev-menu-label");
  timeLabel.htmlFor = "dev-time-slider";
  timeLabel.textContent = "Time of day";
  const timeOutput = createElement("output", "dev-menu-time");
  timeOutput.htmlFor = "dev-time-slider";
  timeHeadingRow.append(timeLabel, timeOutput);
  const timeSlider = createElement("input", "dev-menu-slider");
  timeSlider.id = "dev-time-slider";
  timeSlider.type = "range";
  timeSlider.min = "0";
  timeSlider.max = "23.75";
  timeSlider.step = "0.25";
  const presetRow = createElement("div", "dev-menu-presets");
  const presetButtons = TIME_PRESETS.map((preset) => {
    const button = createElement("button", "dev-menu-preset");
    button.type = "button";
    button.dataset.timeHours = String(preset.hours);
    const label = createElement("span", "dev-menu-preset-label");
    label.textContent = preset.label;
    const time = createElement("span", "dev-menu-preset-time");
    time.textContent = formatTimeOfDay(preset.hours);
    button.append(label, time);
    presetRow.append(button);
    return button;
  });
  const clockRow = createElement("div", "dev-menu-clock-row");
  const clockCopy = createElement("p", "dev-menu-copy");
  clockCopy.textContent = "Hold the light for inspection or let the city clock run.";
  const clockButton = createElement("button", "dev-menu-clock");
  clockButton.type = "button";
  clockRow.append(clockCopy, clockButton);
  timeSection.append(
    timeHeadingRow,
    timeSlider,
    presetRow,
    clockRow,
  );

  const seasonSection = createElement("section", "dev-menu-section");
  const seasonLabel = createElement("p", "dev-menu-label");
  seasonLabel.id = "dev-season-label";
  seasonLabel.textContent = "Season state";
  const seasonButtons = createElement("div", "dev-menu-seasons");
  seasonButtons.setAttribute("role", "group");
  seasonButtons.setAttribute("aria-labelledby", seasonLabel.id);
  const seasonButtonById = new Map<DevMenuSeason, HTMLButtonElement>();
  for (const season of SEASONS) {
    const button = createElement("button", "dev-menu-season");
    button.type = "button";
    button.dataset.season = season.id;
    button.textContent = season.label;
    button.setAttribute("aria-pressed", "false");
    seasonButtons.append(button);
    seasonButtonById.set(season.id, button);
  }
  const seasonNote = createElement("p", "dev-menu-note");
  seasonNote.textContent =
    "Fresh blossom, full leaf, autumn colour and winter snow render immediately.";
  const wetnessHeadingRow = createElement(
    "div",
    "dev-menu-section-heading dev-menu-wetness-heading",
  );
  const wetnessLabel = createElement("label", "dev-menu-label");
  wetnessLabel.htmlFor = "dev-surface-wetness";
  wetnessLabel.textContent = "Surface wetness";
  const wetnessOutput = createElement("output", "dev-menu-value");
  wetnessOutput.setAttribute("for", "dev-surface-wetness");
  wetnessHeadingRow.append(wetnessLabel, wetnessOutput);
  const wetnessSlider = createElement("input", "dev-menu-view-range");
  wetnessSlider.id = "dev-surface-wetness";
  wetnessSlider.type = "range";
  wetnessSlider.min = "0";
  wetnessSlider.max = "1";
  wetnessSlider.step = "0.05";
  wetnessSlider.value = "0";
  wetnessSlider.setAttribute("aria-label", "Surface wetness");
  seasonSection.append(
    seasonLabel,
    seasonButtons,
    seasonNote,
    wetnessHeadingRow,
    wetnessSlider,
  );

  const viewSection = createElement(
    "section",
    "dev-menu-section dev-menu-view",
  );
  const viewHeading = createElement("div", "dev-menu-section-heading");
  const viewLabel = createElement("p", "dev-menu-label");
  viewLabel.id = "dev-view-label";
  viewLabel.textContent = "World-view envelope";
  const activeViewOutput = createElement("output", "dev-menu-view-active");
  viewHeading.append(viewLabel, activeViewOutput);
  const viewModeButtons = createElement("div", "dev-menu-view-modes");
  viewModeButtons.setAttribute("role", "group");
  viewModeButtons.setAttribute("aria-labelledby", viewLabel.id);
  const viewModeButtonById = new Map<WorldViewMode, HTMLButtonElement>();
  for (const mode of VIEW_MODES) {
    const button = createElement("button", "dev-menu-view-mode");
    button.type = "button";
    button.dataset.viewMode = mode.id;
    button.textContent = mode.label;
    button.setAttribute("aria-pressed", "false");
    viewModeButtons.append(button);
    viewModeButtonById.set(mode.id, button);
  }
  const viewControls = createElement("div", "dev-menu-view-controls");
  const viewInputByKey = new Map<WorldViewSettingKey, HTMLInputElement>();
  const viewOutputByKey = new Map<WorldViewSettingKey, HTMLOutputElement>();
  for (const control of VIEW_SETTING_CONTROLS) {
    const row = createElement("div", "dev-menu-view-control");
    const heading = createElement("div", "dev-menu-view-control-heading");
    const label = createElement("label", "dev-menu-view-control-label");
    const id = `dev-view-${control.key}`;
    label.htmlFor = id;
    label.textContent = control.label;
    const output = createElement("output", "dev-menu-view-value");
    output.htmlFor = id;
    heading.append(label, output);
    const input = createElement("input", "dev-menu-view-range");
    const limits = WORLD_VIEW_SETTING_LIMITS[control.key];
    input.id = id;
    input.type = "range";
    input.min = String(limits.min);
    input.max = String(limits.max);
    input.step = String(limits.step);
    input.dataset.viewSetting = control.key;
    row.append(heading, input);
    viewControls.append(row);
    viewInputByKey.set(control.key, input);
    viewOutputByKey.set(control.key, output);
  }
  const viewActions = createElement("div", "dev-menu-view-actions");
  const viewNote = createElement("p", "dev-menu-note");
  viewNote.textContent =
    "Changes apply live and stay in this browser. Larger envelopes use more memory.";
  const resetViewButton = createElement("button", "dev-menu-view-reset");
  resetViewButton.type = "button";
  resetViewButton.textContent = "Reset profile";
  viewActions.append(viewNote, resetViewButton);
  viewSection.append(
    viewHeading,
    viewModeButtons,
    viewControls,
    viewActions,
  );

  const traversalSection = createElement(
    "section",
    "dev-menu-section dev-menu-traversal",
  );
  const traversalLabel = createElement("p", "dev-menu-label");
  traversalLabel.id = "dev-traversal-label";
  traversalLabel.textContent = "Traversal access";
  const traversalButtons = createElement(
    "div",
    "dev-menu-seasons dev-menu-traversal-presets",
  );
  traversalButtons.setAttribute("role", "group");
  traversalButtons.setAttribute("aria-labelledby", traversalLabel.id);
  const traversalButtonByPreset = new Map<
    DevMenuTraversalPreset,
    HTMLButtonElement
  >();
  for (const preset of TRAVERSAL_PRESETS) {
    const button = createElement("button", "dev-menu-season");
    button.type = "button";
    button.dataset.traversalPreset = preset.id;
    button.textContent = preset.label;
    button.setAttribute("aria-pressed", "false");
    traversalButtonByPreset.set(preset.id, button);
    traversalButtons.append(button);
  }
  const traversalNote = createElement("p", "dev-menu-note");
  traversalNote.textContent =
    "Resets the courtyard-key branch, then applies the selected access outcome for route inspection.";
  traversalSection.append(traversalLabel, traversalButtons, traversalNote);

  const travelSection = createElement(
    "section",
    "dev-menu-section dev-menu-travel",
  );
  const travelLabel = createElement("label", "dev-menu-label");
  travelLabel.htmlFor = "dev-landmark-select";
  travelLabel.textContent = "Location transfer";
  const select = createElement("select", "dev-menu-select");
  select.id = "dev-landmark-select";
  for (const destination of destinations) {
    const option = document.createElement("option");
    option.value = destination.id;
    option.textContent = destination.label;
    select.append(option);
  }
  const travelMeta = createElement("p", "dev-menu-travel-meta");
  const travelButton = createElement("button", "dev-menu-travel-button");
  travelButton.type = "button";
  travelButton.textContent = "Travel there";
  const travelStatus = createElement("output", "dev-menu-travel-status");
  travelStatus.setAttribute("aria-live", "polite");
  travelSection.append(
    travelLabel,
    select,
    travelMeta,
    travelButton,
    travelStatus,
  );

  const footer = createElement("footer", "dev-menu-footer");
  footer.textContent =
    "Reviewed approaches · M map · F flight · [ ] time · P clock · F2 field notes";

  panel.append(
    header,
    timeSection,
    seasonSection,
    viewSection,
    traversalSection,
    travelSection,
    footer,
  );
  root.append(toggleButton, panel);

  let open = false;
  let teleporting = false;
  let selectedViewMode: WorldViewMode = "grounded";
  let viewModel: DevMenuViewModel = {
    season: "summer",
    timeOfDayHours: 10,
    frozen: false,
    surfaceWetness01: 0,
    activeViewMode: "grounded",
    viewSettings: DEFAULT_WORLD_VIEW_SETTINGS,
    traversalAccess: { blueGate: false, rooftopShortcut: false },
  };

  function selectedDestination(): LandmarkTeleportDestination {
    return destinations.find(({ id }) => id === select.value) ??
      destinations[0]!;
  }

  function updateTravelMeta(): void {
    const destination = selectedDestination();
    travelMeta.textContent = `Tile ${destination.tileId} · reviewed approach`;
  }

  function render(next: Readonly<DevMenuViewModel>): void {
    viewModel = { ...next };
    timeOutput.textContent = formatTimeOfDay(next.timeOfDayHours);
    timeSlider.value = String(normalizedHour(next.timeOfDayHours));
    clockButton.textContent = next.frozen ? "Held" : "Running";
    clockButton.dataset.frozen = String(next.frozen);
    clockButton.setAttribute(
      "aria-label",
      next.frozen ? "Resume the city clock" : "Hold the city clock",
    );
    const wetness01 = next.surfaceWetness01 ?? 0;
    wetnessSlider.value = String(wetness01);
    wetnessOutput.textContent = `${Math.round(wetness01 * 100)}%`;
    for (const [season, button] of seasonButtonById) {
      const active = season === next.season;
      button.dataset.active = String(active);
      button.setAttribute("aria-pressed", String(active));
    }
    const traversalAccess = next.traversalAccess ?? {
      blueGate: false,
      rooftopShortcut: false,
    };
    const activeTraversalPreset: DevMenuTraversalPreset =
      traversalAccess.rooftopShortcut
        ? "rooftopShortcut"
        : traversalAccess.blueGate
          ? "blueGate"
          : "locked";
    for (const [preset, button] of traversalButtonByPreset) {
      const active = preset === activeTraversalPreset;
      button.dataset.active = String(active);
      button.setAttribute("aria-pressed", String(active));
    }
    const selectedProfile = next.viewSettings[selectedViewMode];
    activeViewOutput.textContent =
      `${selectedViewMode === next.activeViewMode ? "In use" : "Standby"} · ${
        selectedViewMode === "grounded" ? "normal" : "aerial"
      }`;
    for (const [mode, button] of viewModeButtonById) {
      const selected = mode === selectedViewMode;
      button.dataset.active = String(selected);
      button.dataset.current = String(mode === next.activeViewMode);
      button.setAttribute("aria-pressed", String(selected));
    }
    for (const control of VIEW_SETTING_CONTROLS) {
      const value = selectedProfile[control.key];
      const input = viewInputByKey.get(control.key)!;
      const output = viewOutputByKey.get(control.key)!;
      input.value = String(value);
      if (control.key === "fogFarDayMeters") {
        input.max = String(selectedProfile.cameraFarMeters);
      }
      output.textContent = formatViewSetting(control.key, value);
    }
  }

  function setOpen(nextOpen: boolean): void {
    if (open === nextOpen) {
      return;
    }
    open = nextOpen;
    panel.hidden = !open;
    toggleButton.setAttribute("aria-expanded", String(open));
    toggleButton.dataset.open = String(open);
    callbacks.onOpenChange(open);
    if (open) {
      selectedViewMode = viewModel.activeViewMode;
      render(viewModel);
      closeButton.focus({ preventScroll: true });
    }
  }

  function onToggleClick(): void {
    setOpen(!open);
  }

  function onCloseClick(): void {
    setOpen(false);
  }

  function onTimeInput(): void {
    const hours = Number(timeSlider.value);
    timeOutput.textContent = formatTimeOfDay(hours);
    callbacks.onSetTime(hours);
  }

  function onPresetClick(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const hours = Number(button.dataset.timeHours);
    callbacks.onSetTime(hours);
  }

  function onClockClick(): void {
    callbacks.onToggleClock();
  }

  function onSeasonClick(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const season = button.dataset.season as DevMenuSeason;
    callbacks.onSetSeason(season);
  }

  function onWetnessInput(): void {
    callbacks.onSetSurfaceWetness(Number(wetnessSlider.value));
  }

  function onViewModeClick(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    selectedViewMode = button.dataset.viewMode as WorldViewMode;
    render(viewModel);
  }

  function onViewSettingInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const key = input.dataset.viewSetting as WorldViewSettingKey;
    const value = Number(input.value);
    viewOutputByKey.get(key)!.textContent = formatViewSetting(key, value);
    callbacks.onSetViewSetting(selectedViewMode, key, value);
  }

  function onResetViewClick(): void {
    callbacks.onResetViewProfile(selectedViewMode);
  }

  function onTraversalPresetClick(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    callbacks.onSetTraversalPreset(
      button.dataset.traversalPreset as DevMenuTraversalPreset,
    );
  }

  function onSelectChange(): void {
    updateTravelMeta();
    travelStatus.textContent = "";
  }

  async function onTravelClick(): Promise<void> {
    if (teleporting) {
      return;
    }
    teleporting = true;
    panel.setAttribute("aria-busy", "true");
    select.disabled = true;
    travelButton.disabled = true;
    travelButton.textContent = "Relocating…";
    travelStatus.textContent = "";
    try {
      await callbacks.onTeleport(selectedDestination());
      setOpen(false);
    } catch (error) {
      travelStatus.textContent = error instanceof Error
        ? error.message
        : "The transfer could not be completed.";
    } finally {
      teleporting = false;
      panel.removeAttribute("aria-busy");
      select.disabled = false;
      travelButton.disabled = false;
      travelButton.textContent = "Travel there";
    }
  }

  function onGlobalKeyDown(event: KeyboardEvent): void {
    if (event.code === "Backquote") {
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(!open);
    } else if (event.code === "Escape" && open) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
    }
  }

  toggleButton.addEventListener("click", onToggleClick);
  closeButton.addEventListener("click", onCloseClick);
  timeSlider.addEventListener("input", onTimeInput);
  for (const button of presetButtons) {
    button.addEventListener("click", onPresetClick);
  }
  clockButton.addEventListener("click", onClockClick);
  for (const button of seasonButtonById.values()) {
    button.addEventListener("click", onSeasonClick);
  }
  wetnessSlider.addEventListener("input", onWetnessInput);
  for (const button of viewModeButtonById.values()) {
    button.addEventListener("click", onViewModeClick);
  }
  for (const input of viewInputByKey.values()) {
    input.addEventListener("input", onViewSettingInput);
  }
  resetViewButton.addEventListener("click", onResetViewClick);
  for (const button of traversalButtonByPreset.values()) {
    button.addEventListener("click", onTraversalPresetClick);
  }
  select.addEventListener("change", onSelectChange);
  travelButton.addEventListener("click", () => void onTravelClick());
  window.addEventListener("keydown", onGlobalKeyDown, { capture: true });

  updateTravelMeta();
  render(viewModel);

  return {
    update(next): void {
      render(next);
    },
    toggle(): void {
      setOpen(!open);
    },
    close(): void {
      setOpen(false);
    },
    isOpen(): boolean {
      return open;
    },
    dispose(): void {
      if (open) {
        callbacks.onOpenChange(false);
      }
      window.removeEventListener("keydown", onGlobalKeyDown, {
        capture: true,
      });
      wetnessSlider.removeEventListener("input", onWetnessInput);
      toggleButton.remove();
      panel.remove();
    },
  };
}
